/**
 * The overlay charts' model: one series per location for a metric
 * (temperature, strength, maturity, weather), with the target lines,
 * achievement markers and tested range the strength and maturity panels
 * draw. Shared by MultiLocationChart (desktop) and the phone Charts tab.
 *
 * A plain module, not a component: desktop tests mock MultiLocationChart
 * with only its component, so anything the phone code reuses lives here.
 * Highcharts is imported for its types only.
 */
import type Highcharts from 'highcharts/highstock';
import type {
  CalibratedRange,
  ChartDataResponse,
  MaturityDescriptor,
} from '../../services/pours';
import {
  REACHED_OUTSIDE_MARKER_COLOR,
  TREND_ESTIMATE_TOOLTIP,
  achievementPoints,
  outsideTestedSpan,
  strengthRangeCaption,
  targetLabelPriority,
  targetPlotLineId,
  testedTimeSpan,
  type AchievementPoint,
  type TestedSpan,
} from './strengthChart';
import {
  TARGET_OUTSIDE_RANGE_LABEL,
  isTargetOutsideRange,
  maturityValuePrefix,
  targetDisplayName,
} from '../../utils/maturityDisplay';
import { parseUtc } from '../../utils/datetime';

export type MultiChartMetric = 'strength' | 'maturity' | 'temperature' | 'weather';

// Series colour cycle. Distinct enough to tell 4-8 locations apart at a
// glance; loops if a pour has more than this.
export const SERIES_COLORS = [
  '#1976d2', // blue
  '#d32f2f', // red
  '#388e3c', // green
  '#f57c00', // orange
  '#7b1fa2', // purple
  '#0097a7', // teal
  '#c2185b', // pink
  '#5d4037', // brown
];

/** Pluck the [timestamp, value] pairs we want from a per-location chart
 *  response based on the active metric. Returns an empty array when no
 *  usable points exist. */
export const extractSeries = (
  chart: ChartDataResponse,
  metric: MultiChartMetric,
): [number, number][] => {
  if (metric === 'temperature') {
    return (chart.temp || []).filter(
      (p) => p.length >= 2 && p[1] != null,
    ) as [number, number][];
  }
  if (metric === 'strength') {
    // ea rows are [timestamp, strength|null, ttf]. Strength is null without
    // a usable curve (the maturity metric charts those pours) and outside
    // the calibration's plot window.
    return (chart.ea || [])
      .map((p) => [p[0], p[1]])
      .filter((p) => p[1] != null) as [number, number][];
  }
  if (metric === 'maturity') {
    // Raw maturity index (TTF for Nurse Saul, EA for Arrhenius).
    return (chart.ea || [])
      .map((p) => [p[0], p[2] ?? p[1]])
      .filter((p) => p[1] != null) as [number, number][];
  }
  // weather: the evaporation rate (ACI 308R), worked out per concrete
  // location from its temperatures and the pour's weather station readings
  // (PourWeatherTrack, matched by time). One series per location. The
  // station's own readings chart separately (WeatherStationChart).
  return (chart.eva || []).filter(
    (p) => p.length >= 2 && p[1] != null,
  ) as [number, number][];
};

export interface MetricConfig {
  title: string;
  yAxisTitle: string;
  valueSuffix: string;
  valueDecimals: number;
}

// Temperature and weather naming is fixed; strength and maturity follow the
// pour (see metricConfig).
const METRIC_CONFIG: Record<'temperature' | 'weather', MetricConfig> = {
  temperature: {
    title: 'Temperature vs Time',
    yAxisTitle: 'Temperature (°C)',
    valueSuffix: ' °C',
    valueDecimals: 1,
  },
  weather: {
    title: 'Evaporation Rate vs Time',
    yAxisTitle: 'Evaporation (kg/m²/h)',
    valueSuffix: ' kg/m²/h',
    valueDecimals: 2,
  },
};

/** Metric display config. Strength carries the company unit; maturity is
 *  named for the pour's method (Temperature-Time Factor in °C·hrs, or
 *  Equivalent Age at the specified temperature in hrs). */
export const metricConfig = (
  metric: MultiChartMetric,
  maturity: MaturityDescriptor,
  strengthUnit: string,
): MetricConfig => {
  if (metric === 'strength') {
    return {
      title: 'Strength vs Time',
      yAxisTitle: `Strength (${strengthUnit})`,
      valueSuffix: ` ${strengthUnit}`,
      valueDecimals: 1,
    };
  }
  if (metric === 'maturity') {
    return {
      title: `${maturity.index_label} vs Time`,
      yAxisTitle: maturity.axis_title,
      valueSuffix: ` ${maturity.unit}`,
      valueDecimals: 1,
    };
  }
  return METRIC_CONFIG[metric];
};

export const NO_DATA_TEXT = 'No data points logged for any location yet.';

/** One decimal at most: 37.5 stays 37.5, 1000 stays 1000. */
const shortNumber = (v: number): string => String(Math.round(v * 10) / 10);

/** Why the strength panel has nothing to plot. Strength is blank outside the
 *  calibration's plot window and when the calibration has no included
 *  samples (NCS*), so "no data" is only true when no maturity is logged
 *  either. */
export const strengthEmptyText = (
  charts: ChartDataResponse[],
  maturity: MaturityDescriptor,
): string => {
  if (!charts.some((c) => extractSeries(c, 'maturity').length > 0)) return NO_DATA_TEXT;
  const ranges = charts
    .map((c) => c.calibrated_range)
    .filter((r): r is CalibratedRange => !!r);
  if (ranges.some((r) => r.has_curve && !r.has_samples)) {
    return 'No strength to plot: the calibration has no included samples (NCS*).';
  }
  const range = ranges.find(
    (r) => r.has_samples && r.plot_min_m != null && r.plot_max_m != null,
  );
  const span = range
    ? ` (${maturityValuePrefix(maturity)}${shortNumber(range.plot_min_m as number)} to ${shortNumber(range.plot_max_m as number)} ${maturity.unit})`
    : '';
  return `No strength to plot yet: no reading is inside the calibration's plotted range${span}.`;
};

/** A target line on an overlay chart. */
export interface OverlayTargetLine {
  value: number;
  label: string;
  /** Every location carrying this target has achieved it. */
  achieved: boolean;
}

/** The overlay's target lines, drawn as Location Details draws them: each
 *  location's strength targets on the strength chart in the company unit
 *  ("Initial Stressing (32.0 MPa)"), its maturity targets on the maturity
 *  chart with where they sit ("... at 120.0 hrs"), flagged when outside
 *  the calibration's tested range. Locations usually copy the same pour
 *  targets, so one line per distinct label and value: green once every
 *  location carrying it has achieved it, amber until then. */
export const overlayTargetLines = (
  charts: ChartDataResponse[],
  metric: 'strength' | 'maturity',
  maturity: MaturityDescriptor,
  strengthUnit: string,
): OverlayTargetLine[] => {
  const lines = new Map<string, OverlayTargetLine>();
  charts.forEach((chart) => {
    (chart.target ?? []).forEach((t) => {
      const value = metric === 'strength' ? t.target_strength : t.target_ea;
      if (value == null || !(value > 0)) return;
      const name = targetDisplayName(t.target_label?.trim() || null, t.target_strength, strengthUnit);
      const at = metric === 'maturity' ? ` at ${value.toFixed(1)} ${maturity.unit}` : '';
      const flag = isTargetOutsideRange(t, chart.calibrated_range)
        ? `, ${TARGET_OUTSIDE_RANGE_LABEL.toLowerCase()}`
        : '';
      const label = `${name}${at}${flag}`;
      const key = `${label}|${value}`;
      const seen = lines.get(key);
      lines.set(key, { value, label, achieved: (seen ? seen.achieved : true) && !!t.achieved });
    });
  });
  return Array.from(lines.values()).sort((a, b) => a.value - b.value);
};

/** The id of an overlay target line (its place in the sorted list), for the
 *  label declutter. */
const overlayLineId = (index: number): string => targetPlotLineId(`overlay-${index}`);

/** Target lines as dashed y-axis plot lines, coloured as Location Details. */
export const targetPlotLines = (lines: OverlayTargetLine[]): Highcharts.YAxisPlotLinesOptions[] =>
  lines.map((line, i) => {
    const colour = line.achieved ? '#2e7d32' : '#9a6a00';
    return {
      id: overlayLineId(i),
      value: line.value,
      color: colour,
      width: 1.5,
      dashStyle: 'Dash',
      label: {
        text: line.label,
        align: 'right',
        x: -8,
        style: { color: colour, fontSize: '11px', fontWeight: '600' },
      },
      zIndex: 5,
    };
  });

/** Label priority of each overlay line for the declutter: a line some
 *  location has not reached yet ranks above one every location has. */
export const targetLinePriorities = (lines: OverlayTargetLine[]): Record<string, number> =>
  Object.fromEntries(lines.map((line, i) => [overlayLineId(i), targetLabelPriority(!line.achieved)]));

/** The first location calibration with a usable curve and included samples:
 *  the strength panel's tested band and caption (a pour's locations share
 *  one calibration). */
const firstTestedRange = (charts: ChartDataResponse[]): CalibratedRange | null =>
  charts
    .map((c) => c.calibrated_range)
    .find((r): r is CalibratedRange => !!r && r.has_curve && r.has_samples) ?? null;

/** An achieved target with a time that has no marker in `markers` (reached
 *  outside the drawn strength curve). */
const hasUnmarkedTarget = (chart: ChartDataResponse, markers: AchievementPoint[]): boolean => {
  const marked = new Set(markers.flatMap((p) => p.custom.targetIds));
  return (chart.target ?? []).some(
    (t) => t.achieved && !Number.isNaN(parseUtc(t.achievement_time).getTime()) && !marked.has(t.target_id),
  );
};

const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

/** Tooltip of a strength line: the usual reading line (Highcharts' own,
 *  with the panel's decimals and unit), plus a note on the readings outside
 *  the location's tested span. */
export const strengthPointFormatter = (span: TestedSpan | null | undefined) =>
  function formatStrengthPoint(this: Highcharts.Point, pointFormat?: string): string {
    const line = this.tooltipFormatter(pointFormat ?? '');
    return outsideTestedSpan(this.x, span)
      ? `${line}<span style="font-size: 0.8em">${TREND_ESTIMATE_TOOLTIP}</span><br/>`
      : line;
  };

/** Tooltip of an achievement marker: the location, then the marker's lines
 *  (already HTML-escaped by achievementPoints). */
const markerPointFormatter = (locationName: string) =>
  function formatMarkerPoint(this: Highcharts.Point): string {
    const lines = (this.options as { custom?: { lines?: string[] } }).custom?.lines ?? [];
    return [
      `<span style="color:${this.color}">●</span> <b>${escapeHtml(locationName)}</b>`,
      ...lines,
    ].join('<br/>') + '<br/>';
  };

export interface MetricSeries {
  /** The line's series id (the location's markers link to it). */
  id?: string;
  name: string;
  data: [number, number][];
  /** True when this location's channel is a humidity sensor (values are
   *  %RH, plotted on a secondary 0-100% axis in the temperature panel). */
  isHumidity?: boolean;
  /** Strength panel: when this location's maturity was inside the tested
   *  range (the line is dashed and faded outside it). */
  span?: TestedSpan | null;
  /** Strength and maturity panels: where this location reached its
   *  targets. */
  markers?: AchievementPoint[];
}

/** A location's achievement markers: a scatter series linked to its line (so
 *  it hides with the line in the legend), in the line's colour, with an
 *  amber ring on a target outside the tested range. The points carry no
 *  name, so the tooltip header shows their time as on the lines. */
export const markerSeries = (s: MetricSeries, color: string): Highcharts.SeriesScatterOptions => ({
  type: 'scatter',
  name: `${s.name}: targets reached`,
  linkedTo: s.id,
  color,
  data: (s.markers ?? []).map((p) => ({
    x: p.x,
    y: p.y,
    custom: p.custom,
    marker: { lineColor: p.custom.outsideRange ? REACHED_OUTSIDE_MARKER_COLOR : '#ffffff' },
  })),
  // The panel's plotOptions turn markers off for the lines.
  marker: {
    enabled: true,
    symbol: 'circle',
    radius: 5,
    lineWidth: 2,
    lineColor: '#ffffff',
    states: { hover: { enabled: true, radius: 7 } },
  },
  dataGrouping: { enabled: false },
  showInLegend: false,
  zIndex: 6,
  tooltip: {
    headerFormat: '<span style="font-size: 0.8em">{ucfirst point.key}</span><br/>',
    pointFormatter: markerPointFormatter(s.name),
  },
});

/** One location's chart response, with what the panels need to place it. */
export interface LocationChart {
  id: string;
  name: string;
  isHumidity: boolean;
  /** The location accrues maturity, so it belongs on the strength and
   *  maturity panels. */
  hasMaturity: boolean;
  chart: ChartDataResponse;
}

/** One metric's panel: its series per location and what the strength and
 *  maturity panels draw over them. */
export interface OverlayPanel {
  metric: MultiChartMetric;
  seriesData: MetricSeries[];
  /** Strength: why there is nothing to plot (strengthEmptyText). */
  emptyText: string | undefined;
  /** Strength and maturity: the target lines. */
  targetLines: OverlayTargetLine[] | undefined;
  /** Strength: the calibration's tested range, shaded on the axis. */
  strengthRange: CalibratedRange | null;
  /** Strength: the tested range in words (strengthRangeCaption). */
  caption: string | undefined;
}

/**
 * Slice each requested metric (`metricsKey`, comma-joined) out of the shared
 * per-location responses, with what the strength and maturity panels draw
 * over the readings: each location's tested span and achievement markers,
 * the target lines, and the strength panel's tested range and caption.
 */
export const buildOverlayPanels = (
  charts: LocationChart[],
  metricsKey: string,
  meta: MaturityDescriptor,
  unit: string,
): OverlayPanel[] => {
  const metricList = metricsKey.split(',') as MultiChartMetric[];
  const maturityCharts = charts.filter((c) => c.hasMaturity).map((c) => c.chart);
  const strengthRange = firstTestedRange(maturityCharts);
  return metricList.map((metric) => {
    const targetMetric = metric === 'strength' || metric === 'maturity' ? metric : null;
    // A target reached outside a location's drawn strength curve has no
    // marker on the strength panel; the caption says where it is marked.
    let offCurve = false;
    const seriesData = charts
      // Only locations that accrue maturity belong on the strength and
      // maturity panels. Humidity channels have no evaporation either;
      // they stay on the temperature panel, plotted on the secondary
      // %RH axis.
      .filter((c) =>
        metric === 'temperature'
          ? true
          : metric === 'weather'
            ? !c.isHumidity
            : c.hasMaturity,
      )
      .map((c): MetricSeries => {
        const data = extractSeries(c.chart, metric);
        const series: MetricSeries = {
          id: `location-${c.id}`,
          name: c.name,
          // Humidity only applies to the temperature panel (a humidity
          // channel's readings arrive through its `temp` series).
          isHumidity: metric === 'temperature' && c.isHumidity,
          data,
        };
        if (!targetMetric) return series;
        const markers = achievementPoints(c.chart.target, data, {
          chart: targetMetric,
          strengthUnit: unit,
          meta,
          range: c.chart.calibrated_range,
          concreteStart: c.chart.location_details?.ea_started ?? c.chart.ea_start,
          monitoringStart: c.chart.location_details?.logging_started,
        });
        if (targetMetric === 'maturity') return { ...series, markers };
        if (hasUnmarkedTarget(c.chart, markers)) offCurve = true;
        return { ...series, markers, span: testedTimeSpan(c.chart.ea, c.chart.calibrated_range) };
      })
      .filter((s) => s.data.length > 0);
    return {
      metric,
      seriesData,
      emptyText: metric === 'strength' ? strengthEmptyText(maturityCharts, meta) : undefined,
      targetLines: targetMetric
        ? overlayTargetLines(maturityCharts, targetMetric, meta, unit)
        : undefined,
      strengthRange: metric === 'strength' ? strengthRange : null,
      caption:
        metric === 'strength'
          ? strengthRangeCaption(strengthRange, meta, unit, {
              offCurveTargets: offCurve && metricList.includes('maturity'),
            }) || undefined
          : undefined,
    };
  });
};
